import {
  MultiPolygon2,
  NumberWithPoints2Result,
  Point2,
  Polyline2,
  ResultType,
} from '../../geometric-types';

const EPSILON = 1e-9;

function areSamePoint(a: Point2, b: Point2): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

function uniqueVerticesForClosedRing(ring: Polyline2): Polyline2 {
  // Remove duplicated closing point if present
  let vertices =
    ring.length >= 2 && areSamePoint(ring[0], ring[ring.length - 1])
      ? ring.slice(0, ring.length - 1)
      : ring;

  // Deduplicate consecutive identical vertices inside the ring
  const deduped: Polyline2 = [];
  for (let i = 0; i < vertices.length; i++) {
    const current = vertices[i];
    const prev = deduped.length ? deduped[deduped.length - 1] : undefined;
    if (!prev || !areSamePoint(prev, current)) {
      deduped.push(current);
    }
  }
  return deduped;
}

/**
 * Calculate the signed area of a polygon ring.
 * Positive = counter-clockwise, Negative = clockwise
 */
function signedArea(vertices: Polyline2): number {
  let area = 0;
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += vertices[i][0] * vertices[j][1];
    area -= vertices[j][0] * vertices[i][1];
  }
  return area / 2;
}

/**
 * Find convex corners (exterior angle < 180°) in a ring.
 * For a CCW ring, convex corners have positive cross product.
 * For a CW ring, convex corners have negative cross product.
 * Returns the corner points.
 */
function findConvexCornersInRing(ring: Polyline2): Point2[] {
  if (ring.length < 4) return [];

  const vertices = uniqueVerticesForClosedRing(ring);
  if (vertices.length < 3) return [];

  const area = signedArea(vertices);
  const isCCW = area > 0;

  const corners: Point2[] = [];
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
    const prev = vertices[(i - 1 + n) % n];
    const curr = vertices[i];
    const next = vertices[(i + 1) % n];

    const v1x = curr[0] - prev[0];
    const v1y = curr[1] - prev[1];
    const v2x = next[0] - curr[0];
    const v2y = next[1] - curr[1];

    const cross = v1x * v2y - v1y * v2x;
    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);

    if (len1 < EPSILON || len2 < EPSILON) continue;
    if (Math.abs(cross) < EPSILON) continue; // Collinear, not a corner

    // For CCW polygon: positive cross = left turn = convex corner
    // For CW polygon: negative cross = left turn = convex corner
    const isConvex = isCCW ? cross > 0 : cross < 0;
    if (isConvex) corners.push(curr);
  }

  return corners;
}

/**
 * Calculate the number of convex (external) corners in a polygon.
 * A convex corner is one where the interior angle is less than 180°.
 * Also returns the corner points for visualization.
 */
export function calculateSheetShapeNumberOfConvexCorners(
  shape: MultiPolygon2
): NumberWithPoints2Result {
  const allCorners: Point2[] = [];

  for (const polygon of shape.polygons) {
    const corners = findConvexCornersInRing(polygon.exterior);
    allCorners.push(...corners);
  }

  return {
    type: ResultType.NUMBER,
    value: allCorners.length,
    points: allCorners,
  };
}
